import React, { useState } from "react";
import { ChevronDown, Warehouse, Search, Plus, Rows3, Rocket, Flag, Eye, Sun, Moon, Filter } from "lucide-react";

const OUT = { fontFamily: "Outfit, sans-serif" };
const MONO = { fontFamily: "JetBrains Mono, monospace" };

const STATUS = {
  online: { dot: "#10B981", tintL: "rgba(16,185,129,0.10)", txtL: "#065F46", tintD: "rgba(16,185,129,0.15)", txtD: "#34D399", bd: "rgba(16,185,129,0.4)" },
  pruefen: { dot: "#F59E0B", tintL: "rgba(245,158,11,0.10)", txtL: "#92400E", tintD: "rgba(245,158,11,0.15)", txtD: "#FBBF24", bd: "rgba(245,158,11,0.4)" },
  offline: { dot: "#EF4444", tintL: "rgba(239,68,68,0.10)", txtL: "#991B1B", tintD: "rgba(239,68,68,0.15)", txtD: "#F87171", bd: "rgba(239,68,68,0.4)" },
};

const KPIS = [
  { label: "GERÄTE GESAMT", value: "287", tone: "text" },
  { label: "ONLINE", value: "184", tone: "online" },
  { label: "ERREICHBAR (TV)", value: "23", tone: "pruefen" },
  { label: "OFFLINE", value: "9", tone: "offline" },
  { label: "KEIN AGENT", value: "94", tone: "text" },
];

const ROWS = [
  ["PC000140", "MHGC02-01", "MHGC02", "MANNHEIM SOUTH NECKARAU", "1068414903", "v43", "10.09. 15:14", "online"],
  ["PC000186", "QULW02-01", "QULW02", "HEIDENHEIM", "1063795164", "v43", "10.09. 15:14", "pruefen"],
  ["PC000120", "HNUC03-01", "HNUC03", "HANAU USE ZNFC02", "481955255", "v43", "10.09. 15:14", "online"],
  ["PC000261", "ZOON01-01", "ZOON01", "MUELHEIM / RUHR", "450347774", "v43", "09.09. 22:01", "offline"],
  ["PC000093", "FUHC01-01", "FUHC01", "FUERTH", "511426480", "v43", "10.09. 15:14", "online"],
  ["PC000246", "SANK02-01", "SANK02", "SANKT AUGUSTIN", "104827054", "v43", "10.09. 15:13", "pruefen"],
  ["PC000063", "EIBC02-01", "EIBC02", "EISENACH", "1072203260", "v43", "10.09. 15:13", "online"],
  ["PC000311", "BYUC01-01", "BYUC01", "BAYREUTH", "—", "—", "08.09. 09:12", "offline"],
];

export default function StyleGuide() {
  const [dark, setDark] = useState(false);
  const [tab, setTab] = useState("Übersicht");
  const [dense, setDense] = useState(false);
  const tabs = ["Übersicht", "Standorte", "Live-Aktivität", "Berichte", "Tickets", "Benutzer"];

  const T = dark
    ? { appBg: "#121417", surface: "#1B1E23", subtle: "#23272E", border: "#2D323B", text: "#F8FAFC", text2: "#94A3B8", text3: "#7A8699", codeBg: "#23272E", codeText: "#CBD5E1", rowAlt: "#171A1F", head: "#1B1E23", chipBg: "#1B1E23" }
    : { appBg: "#F8FAFC", surface: "#FFFFFF", subtle: "#F1F5F9", border: "#E2E8F0", text: "#0F172A", text2: "#475569", text3: "#64748B", codeBg: "#F1F5F9", codeText: "#334155", rowAlt: "#F8FAFC", head: "#FFFFFF", chipBg: "#FFFFFF" };

  const pad = dense ? "6px 12px" : "12px 12px";

  const StatusDot = ({ tone }) => (
    <span className={`inline-block w-2.5 h-2.5 rounded-full ${tone !== "offline" ? "pulse-dot" : ""}`} style={{ background: STATUS[tone].dot }} />
  );
  const TvBtn = ({ tone, id }) => {
    if (id === "—") return <span style={{ ...MONO, color: T.text3 }} className="text-[13px]">—</span>;
    const s = STATUS[tone];
    return (
      <span style={{ ...MONO, background: dark ? s.tintD : s.tintL, color: dark ? s.txtD : s.txtL, borderColor: s.bd }} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[12px] border">
        <span className={`inline-block w-2 h-2 rounded-full ${tone !== "offline" ? "pulse-dot" : ""}`} style={{ background: s.dot }} /> {id}
      </span>
    );
  };
  const chip = (label) => (
    <span key={label} style={{ background: T.chipBg, borderColor: T.border, color: T.text2 }} className="px-3 py-1.5 rounded-lg text-sm border whitespace-nowrap">{label}</span>
  );

  return (
    <div style={{ background: T.appBg, minHeight: "100vh", fontFamily: "Inter, sans-serif" }} data-testid="dashboard-preview">
      {/* Anthracite header */}
      <div className="px-6 h-[60px] flex items-center gap-1 sticky top-0 z-10" style={{ background: "#1B1E23", color: "#F8FAFC" }}>
        <div className="flex items-center gap-2 mr-4">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "#C00000" }}>
            <span style={{ ...OUT, fontWeight: 800 }} className="text-white text-sm leading-none -tracking-wider">//</span>
          </div>
          <span style={OUT} className="font-bold text-[15px] hidden sm:inline"><span className="text-white">Fleet</span><span style={{ color: "#C00000" }}>Area</span></span>
        </div>
        {tabs.map((tb) => (
          <button key={tb} onClick={() => setTab(tb)}
            className={`px-3 py-2 rounded-md text-sm transition-colors ${tab === tb ? "text-white font-semibold" : "font-medium"}`}
            style={tab === tb ? { background: "#23272E", borderBottom: "2px solid #C00000", borderRadius: "6px 6px 0 0" } : { color: "#94A3B8" }}>
            {tb}
          </button>
        ))}
        <button className="px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1.5" style={{ color: "#94A3B8" }}>
          <Warehouse className="w-4 h-4" /> Geräte <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setDark((v) => !v)} data-testid="preview-theme-toggle" title="Hell/Dunkel" className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-white" style={{ background: "#C00000" }}>
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />} {dark ? "Hell" : "Dunkel"}
          </button>
          <span style={{ ...MONO, borderColor: "rgba(255,255,255,0.2)", color: "#94A3B8" }} className="text-xs px-2.5 py-1.5 rounded-md border">DE</span>
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
        <div style={{ ...MONO, color: "#C00000" }} className="text-[11px] uppercase tracking-widest">Dashboard-Vorschau · neues Theme {dark ? "· Dunkel" : "· Hell"}</div>

        {/* KPI bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {KPIS.map((k) => {
            const color = k.tone === "text" ? T.text : (dark ? STATUS[k.tone].txtD : STATUS[k.tone].dot);
            return (
              <div key={k.label} className="rounded-2xl border px-4 py-4" style={{ background: T.surface, borderColor: T.border }}>
                <div style={{ ...MONO, color: T.text3 }} className="text-[11px] font-medium tracking-wider">{k.label}</div>
                <div style={{ ...OUT, fontSize: 24, fontWeight: 700, color }} className="mt-1">{k.value}</div>
              </div>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.text3 }} />
            <div className="w-full rounded-lg pl-9 pr-3 py-2.5 text-sm border" style={{ background: T.subtle, borderColor: T.border, color: T.text3 }}>Suche: Hostname, Code, TeamViewer-ID …</div>
          </div>
          <button className="p-2.5 rounded-lg border" style={{ background: T.surface, borderColor: T.border, color: T.text2 }}><Filter className="w-4 h-4" /></button>
          <button onClick={() => setDense((v) => !v)} className="p-2.5 rounded-lg border" style={dense ? { background: "rgba(192,0,0,0.12)", borderColor: "rgba(192,0,0,0.4)", color: "#C00000" } : { background: T.surface, borderColor: T.border, color: T.text2 }}><Rows3 className="w-4 h-4" /></button>
          <button className="p-2.5 rounded-lg border" style={{ background: T.surface, borderColor: T.border, color: T.text2 }}><Rocket className="w-4 h-4" /></button>
          <div className="flex-1" />
          <button className="px-4 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5" style={{ background: "#C00000" }}><Plus className="w-4 h-4" /> Neue Station</button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["Agent: Alle (287)", "Status: Alle", "Gruppe: Alle", "TV nicht im Konto (23)", "Bundesland: Alle", "Hersteller: Alle"].map(chip)}
        </div>

        {/* Table */}
        <div className="rounded-2xl border overflow-hidden" style={{ background: T.surface, borderColor: T.border }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ color: T.text }}>
              <thead>
                <tr style={{ background: T.head, borderBottom: `1px solid ${T.border}`, color: T.text3 }} className="text-left text-[12px] font-medium">
                  {["", "Aktionen", "AssetID", "Geräte-Code", "Standort-ID", "Standort-Name", "TeamViewer-ID", "Skript", "Zuletzt gesehen"].map((h, i) => (
                    <th key={i} style={{ padding: "12px" }} className="whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r, i) => (
                  <tr key={r[0]} style={{ background: i % 2 ? T.rowAlt : T.surface, borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: pad }}><StatusDot tone={r[7]} /></td>
                    <td style={{ padding: pad }}>
                      <div className="flex items-center gap-2" style={{ color: T.text3 }}>
                        <Flag className="w-4 h-4" /><Eye className="w-4 h-4" />
                      </div>
                    </td>
                    <td style={{ padding: pad }}><span style={{ ...MONO, color: T.text2 }} className="text-[13px]">{r[0]}</span></td>
                    <td style={{ padding: pad }}><span style={{ ...MONO, background: T.codeBg, borderColor: T.border, color: T.codeText }} className="text-[13px] px-2 py-1 rounded border">{r[1]}</span></td>
                    <td style={{ padding: pad }}><span style={{ ...MONO, color: "#C00000" }} className="text-[13px]">{r[2]}</span></td>
                    <td style={{ padding: pad }}><span style={{ color: T.text, fontWeight: 500 }}>{r[3]}</span></td>
                    <td style={{ padding: pad }}><TvBtn tone={r[7]} id={r[4]} /></td>
                    <td style={{ padding: pad }}><span style={{ ...MONO, color: T.text2 }} className="text-[13px]">{r[5]}</span></td>
                    <td style={{ padding: pad }}><span style={{ color: T.text2 }} className="text-[13px]">{r[6]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
